import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input } from "@bookdental/ui";
import { AJUDA_PACIENTES } from "@/lib/ajuda/dialogos/pacientes";
import { COR_NEUTRA } from "./cores-do-plano";

export type ItemDaBase = {
  id: string;
  nome: string;
  cor: string | null;
  unidade: string | null;
  cobradoPorDente: boolean;
  especialidade: { code: string; label: string };
};

/** Por que escolher não adiciona ao catálogo — muda o aviso; `null` quando adiciona. */
export type BloqueioDoCatalogo = "permissao" | "plano" | null;

const AVISO: Record<"livre" | "permissao" | "plano", string> = {
  livre: "Procedimentos que ainda não estão no seu catálogo. Ao escolher, ele entra no catálogo (com o preço em branco).",
  permissao: "Estes procedimentos não estão no catálogo da clínica. Peça a quem gerencia a clínica para adicionar; por enquanto entra só neste plano.",
  plano: "Catálogo de procedimentos é do plano pago: o procedimento entra só neste plano.",
};

const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
const ehDaFace = (b: ItemDaBase) => b.especialidade.code === "HARMONIZACAO_OROFACIAL";

/**
 * "Novo procedimento" do plano: busca na base o que a clínica ainda não tem (primeiro os do modo —
 * harmonização na Face, o resto nos Dentes); pra nome que não existe, abre o cadastro do procedimento.
 */
export function NovoProcedimentoDialog({
  aberto,
  onFechar,
  modoFace,
  daBase,
  nomesDoCatalogo,
  bloqueio,
  ocupado,
  onEscolher,
  onCriar,
}: {
  aberto: boolean;
  onFechar: () => void;
  modoFace: boolean;
  daBase: ItemDaBase[];
  nomesDoCatalogo: string[];
  bloqueio: BloqueioDoCatalogo;
  ocupado: boolean;
  onEscolher: (b: ItemDaBase) => void;
  onCriar?: (nome: string) => void;
}) {
  const [busca, setBusca] = useState("");
  const [todos, setTodos] = useState(false);
  const termo = semAcento(busca.trim());
  const lista = useMemo(() => {
    const doModo = todos ? daBase : daBase.filter((b) => ehDaFace(b) === modoFace);
    return termo ? doModo.filter((b) => semAcento(`${b.nome} ${b.especialidade.label}`).includes(termo)) : doModo;
  }, [daBase, todos, modoFace, termo]);
  const existe = [...nomesDoCatalogo, ...daBase.map((b) => b.nome)].some((n) => semAcento(n.trim()) === termo);
  const fechar = () => {
    onFechar();
    setTimeout(() => {
      setBusca("");
      setTodos(false);
    }, 200);
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-lg border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_PACIENTES.novoProcedimento}>Novo procedimento</DialogTitle>
          <DialogDescription>{AVISO[bloqueio ?? "livre"]}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar procedimento" className="pl-9" />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-muted-foreground">
          <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} className="h-4 w-4 accent-brand-600" />
          Mostrar toda a base (não só {modoFace ? "harmonização" : "os de dentes"})
        </label>
        <ul className="max-h-72 divide-y divide-zinc-100 overflow-auto rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
          {lista.length === 0 && <li className="px-3 py-4 text-center text-sm text-zinc-500">Nada na base com esse nome.</li>}
          {lista.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => {
                  onEscolher(b);
                  fechar();
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-brand-50 disabled:opacity-50 dark:hover:bg-primary/10"
              >
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: b.cor || COR_NEUTRA }} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-zinc-900 dark:text-foreground">{b.nome}</span>
                  <span className="block text-[11px] text-zinc-500">{b.especialidade.label}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {onCriar && termo.length >= 2 && !existe && (
          <button
            type="button"
            disabled={ocupado}
            onClick={() => {
              onCriar(busca.trim());
              fechar();
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-brand-300 px-3 py-2.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-primary"
          >
            <Plus className="h-4 w-4" /> Criar “{busca.trim()}” no catálogo
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
